import {
    Scale,
    Tags,
    Zap,
    Plane,
    BedDouble,
    Sparkles,
    Smartphone,
} from "lucide-react";
import { MaskedLine, Reveal, usePageTitle } from "../components/Motion";
import { Seo } from "../components/Seo";
import PlayStoreButton from "../components/PlayStoreButton";

const FEATURES = [
    {
        icon: Scale,
        title: "Product Price Comparison",
        desc: "Compare relevant products and prices across supported platforms before making a purchase.",
        detail: "Look up a product in the app and review how it is priced across supported platforms, so you can decide where to buy with more context.",
        testId: "features-detail-price-comparison",
    },
    {
        icon: Tags,
        title: "Coupons & Deals",
        desc: "Discover available coupons, offers and deals from supported merchants.",
        detail: "Browse offers surfaced from supported merchants and check which ones apply to what you plan to buy.",
        testId: "features-detail-coupons-deals",
    },
    {
        icon: Zap,
        title: "Quick Commerce",
        desc: "Explore and compare products and prices across supported quick-commerce platforms.",
        detail: "Everyday essentials move fast. SahiKhoj helps you look across supported quick-commerce platforms in one place.",
        testId: "features-detail-quick-commerce",
    },
    {
        icon: Plane,
        title: "Flights & Travel",
        desc: "Search and explore flight and travel options through the SahiKhoj app.",
        detail: "Explore flight and travel options inside the app and review what is available before you book on the respective platform.",
        testId: "features-detail-flights-travel",
    },
    {
        icon: BedDouble,
        title: "Hotels",
        desc: "Explore hotel options through the SahiKhoj travel experience.",
        detail: "Browse hotel options as part of the SahiKhoj travel experience and shortlist stays that fit your plans.",
        testId: "features-detail-hotels",
    },
    {
        icon: Sparkles,
        title: "AI Search",
        desc: "Search in plain words — AI Search understands what you are looking for and helps you find suitable options faster.",
        detail: "Tell the app what you need in everyday language. AI Search understands your requirement and surfaces suitable options from supported platforms, so you spend less time filtering.",
        testId: "features-detail-ai-assistance",
    },
];

const FeaturesPage = () => {
    usePageTitle("Features — SahiKhoj");
    return (
        <>
            <Seo
                title="Features — SahiKhoj"
                description="SahiKhoj features: deals and coupon discovery, AI Search, product price comparison, quick commerce, flight search and hotel comparison."
                path="/features"
            />
            <section data-testid="features-hero" className="hero-glow bg-ink pb-20 pt-40 text-cream sm:pb-28 lg:pt-48">
                <div className="mx-auto max-w-7xl px-6 sm:px-8 lg:px-12">
                    <p className="flex items-center gap-3 font-mono text-xs uppercase tracking-[0.24em] text-mist">
                        <span className="h-px w-10 bg-saffron" />
                        App features
                    </p>
                    <h1 className="mt-7 max-w-4xl font-display text-5xl font-semibold leading-[1.05] tracking-tighter sm:text-6xl lg:text-7xl">
                        <MaskedLine delay={0.2}>Everything you need,</MaskedLine>
                        <MaskedLine delay={0.35} className="text-saffron">
                            in one app.
                        </MaskedLine>
                    </h1>
                    <p className="mt-7 max-w-xl text-base leading-relaxed text-mist sm:text-lg">
                        A closer look at what the SahiKhoj mobile app does. These
                        features are not available on this website — they live inside
                        the app.
                    </p>
                </div>
            </section>

            <section className="bg-cream py-24 sm:py-32">
                <div className="mx-auto max-w-7xl px-6 sm:px-8 lg:px-12">
                    <div className="space-y-6">
                        {FEATURES.map(({ icon: Icon, title, desc, detail, badge, testId }, i) => (
                            <Reveal key={title} delay={0.05}>
                                <article
                                    data-testid={testId}
                                    className="group grid grid-cols-1 gap-8 rounded-3xl border border-ink/5 bg-white p-8 shadow-sm transition-all duration-300 hover:-translate-y-1 hover:shadow-xl sm:p-12 lg:grid-cols-12"
                                >
                                    <div className="flex items-start justify-between lg:col-span-3 lg:flex-col lg:justify-start lg:gap-6">
                                        <span className="text-outline font-display text-6xl font-semibold leading-none">
                                            0{i + 1}
                                        </span>
                                        <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-saffron-soft transition-colors duration-300 group-hover:bg-saffron">
                                            <Icon className="h-5 w-5 text-saffron transition-colors duration-300 group-hover:text-white" />
                                        </span>
                                    </div>
                                    <div className="lg:col-span-9">
                                        <div className="flex flex-wrap items-center gap-3">
                                            <h2 className="font-display text-2xl font-semibold tracking-tight text-ink sm:text-3xl">
                                                {title}
                                            </h2>
                                            {badge && (
                                                <span className="rounded-full bg-india/10 px-3 py-1 font-mono text-[10px] uppercase tracking-[0.16em] text-india">
                                                    {badge}
                                                </span>
                                            )}
                                        </div>
                                        <p className="mt-4 font-display text-lg font-medium leading-snug tracking-tight text-india">
                                            {desc}
                                        </p>
                                        <p className="mt-3 max-w-2xl text-sm leading-relaxed text-smoke sm:text-base">
                                            {detail}
                                        </p>
                                        <p className="mt-5 inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.18em] text-smoke/70">
                                            <Smartphone className="h-3.5 w-3.5 text-saffron" />
                                            Inside the SahiKhoj mobile app
                                        </p>
                                    </div>
                                </article>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            <section className="bg-cream px-6 pb-24 sm:px-8 sm:pb-32 lg:px-12">
                <Reveal>
                    <div className="relative mx-auto max-w-7xl overflow-hidden rounded-[2.5rem] bg-india px-8 py-16 text-center sm:px-12 sm:py-20">
                        <div className="pointer-events-none absolute -right-20 -top-20 h-64 w-64 rounded-full bg-saffron/25 blur-3xl" />
                        <h2 className="font-display text-3xl font-semibold tracking-tight text-cream sm:text-4xl">
                            Ready when the app is.
                        </h2>
                        <p className="mx-auto mt-4 max-w-md text-sm leading-relaxed text-cream/75">
                            SahiKhoj is launching soon on Google Play. All features
                            above will be available inside the app.
                        </p>
                        <div className="mt-8 flex justify-center">
                            <PlayStoreButton testId="features-play-store-button" />
                        </div>
                    </div>
                </Reveal>
            </section>
        </>
    );
};

export default FeaturesPage;
